import React from 'react';
import { useIdioma } from '../../i18n/idioma';
import { money } from '../../utils/formatCurrency';

/**
 * Primitivas de tabla — Sistema 2.0.
 *
 * Reglas del sistema:
 *  - Encabezados: .label-ui (condensada, 11px, acero-600), separados por una
 *    hairline; NO fondo gris.
 *  - Filas: separadas por hairline acero-100; hover = tinte acero-50.
 *  - Celdas: 13-14px, altura de fila ~44px (py-3), primera columna semibold.
 *  - Números/dinero: SIEMPRE <MoneyCell> o font-data tabular-nums, a la derecha.
 *  - Acciones por fila: <RowActions> aparece en hover (y con foco de teclado).
 *
 * Uso:
 *   <TableWrap>
 *     <Table>
 *       <THead><Th>Nombre</Th><Th align="right">{t('comun.total')}</Th></THead>
 *       <TBody>
 *         <Tr onClick={…}><Td primary>Ana</Td><MoneyCell value={1200} /></Tr>
 *       </TBody>
 *     </Table>
 *   </TableWrap>
 */

/**
 * `md:overflow-x-clip` y no `overflow-x-auto` a secas.
 *
 * `overflow-x: auto` convierte a este div en CONTENEDOR DE SCROLL, y entonces
 * el `top` de una cabecera `sticky` se mide desde el borde de ESTE div, no
 * desde la ventana. Con `top: var(--rp-cabecera)` —267 px, la altura de la
 * banda clavada de la página— la cabecera se iba 267 px por debajo del inicio
 * de la tarjeta y aparecía DEBAJO de las filas, o pegada al fondo de la tabla.
 * Medido: con `auto` la cabecera caía en y=513 teniendo la primera fila en
 * y=329; con `clip`, en y=293, encima de todo.
 *
 * `clip` no crea contenedor de scroll, así que el `sticky` vuelve a medirse
 * contra la ventana. Va desde `md` porque es donde la cabecera se clava; en el
 * teléfono no se clava y ahí sigue haciendo falta el arrastre horizontal.
 */
export const TableWrap: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...props }) => (
    <div className={`w-full overflow-x-auto md:overflow-x-clip ${className}`} {...props}>
        {children}
    </div>
);

export const Table: React.FC<React.TableHTMLAttributes<HTMLTableElement>> = ({ className = '', children, ...props }) => (
    <table className={`w-full text-left border-collapse ${className}`} {...props}>
        {children}
    </table>
);

interface THeadProps extends React.HTMLAttributes<HTMLTableSectionElement> {
    /**
     * Deja la fila de títulos a la vista mientras la lista baja.
     *
     *   'pagina' — la tabla vive en el flujo de la página y encima hay una
     *              banda clavada; se pega a su borde inferior (`--rp-cabecera`).
     *   'panel'  — la tabla vive dentro de un panel con scroll propio (un
     *              cajón, un modal); se pega al borde del panel.
     *
     * NO va por defecto, y esto es deliberado. Antes `THead` llevaba
     * `sticky top-0` desde el primer commit y nunca llegó a clavarse: el
     * scrollport más cercano era el div de App.tsx, que por `overflow-x-hidden`
     * contaba como contenedor pero jamás scrolleaba. Al arreglar el scroll de
     * la página, ese `sticky` se habría encendido de golpe en las 18 vistas que
     * usan THead, sin que nadie lo pidiera. Así que se pide tabla por tabla.
     *
     * Dónde vale la pena: donde la lista es larga y las columnas se parecen. En
     * los 790 materiales, «Tu costo» y «Markup» son dos columnas de números
     * seguidas — sin encabezado a la vista no se sabe cuál estás leyendo.
     */
    pegado?: 'pagina' | 'panel';
}

export const THead: React.FC<THeadProps> = ({ pegado, className = '', children, ...props }) => {
    /* Desde `md` para arriba, nunca en el teléfono.
     *
     * En un teléfono ya hay dos cosas fijas arriba —la barra de la app (56 px)
     * y la fila de pestañas—, y sumar el encabezado de la tabla dejaría un
     * tercio de la pantalla ocupado por cosas que no son la lista. Además
     * `--rp-cabecera` solo lo publica la banda cuando está clavada, y en el
     * teléfono no lo está: el encabezado se pegaría a 0 y quedaría debajo de la
     * barra de la app. */
    const clavado = pegado === 'pagina'
        ? 'md:sticky md:top-[var(--rp-cabecera,0px)] z-10'
        : pegado === 'panel'
            ? 'sticky top-0 z-10'
            : '';
    return (
        /* El fondo blanco es obligatorio cuando se clava: sin él las filas se
           verían pasar por detrás de los títulos. */
        <thead className={`bg-white ${clavado} ${className}`} {...props}>
            <tr className="h-9 border-b border-acero-200">{children}</tr>
        </thead>
    );
};

interface ThProps extends React.ThHTMLAttributes<HTMLTableCellElement> {
    align?: 'left' | 'right' | 'center';
    /** Oculta la columna en móvil */
    hideOnMobile?: boolean;
}

export const Th: React.FC<ThProps> = ({ align = 'left', hideOnMobile = false, className = '', children, ...props }) => (
    <th
        className={`label-ui font-normal whitespace-nowrap py-0 align-middle first:pl-1 last:pr-1 px-3
            ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}
            ${hideOnMobile ? 'hidden md:table-cell' : ''} ${className}`}
        {...props}
    >
        {children}
    </th>
);

export const TBody: React.FC<React.HTMLAttributes<HTMLTableSectionElement>> = ({ className = '', children, ...props }) => (
    <tbody className={`divide-y divide-acero-100 ${className}`} {...props}>
        {children}
    </tbody>
);

interface TrProps extends React.HTMLAttributes<HTMLTableRowElement> {
    /** Marca la fila como seleccionada */
    selected?: boolean;
    /** 'estandar' 44px · 'compacta' 36px (partidas, horas) · 'comoda' 56px (avatar + 2ª línea) */
    density?: 'estandar' | 'compacta' | 'comoda';
}

const ROW_H: Record<NonNullable<TrProps['density']>, string> = {
    estandar: 'h-11',
    compacta: 'h-9',
    comoda: 'h-14',
};

export const Tr: React.FC<TrProps> = ({ selected = false, density = 'estandar', onClick, className = '', children, ...props }) => (
    <tr
        onClick={onClick}
        className={`group rp-row ${ROW_H[density]}
            ${onClick ? 'cursor-pointer' : ''}
            ${selected ? 'bg-brand-50/60' : onClick ? 'hover:bg-acero-100/70' : ''} ${className}`}
        {...props}
    >
        {children}
    </tr>
);

interface TdProps extends React.TdHTMLAttributes<HTMLTableCellElement> {
    align?: 'left' | 'right' | 'center';
    /** Columna principal — texto navy semibold */
    primary?: boolean;
    /** Texto atenuado (metadatos) */
    muted?: boolean;
    hideOnMobile?: boolean;
}

export const Td: React.FC<TdProps> = ({
    align = 'left', primary = false, muted = false, hideOnMobile = false, className = '', children, ...props
}) => (
    <td
        className={`py-0 px-3 first:pl-1 last:pr-1 text-[13px] align-middle
            ${align === 'right' ? 'text-right' : align === 'center' ? 'text-center' : 'text-left'}
            ${primary ? 'font-medium text-navy-950' : muted ? 'text-acero-500' : 'text-acero-700'}
            ${hideOnMobile ? 'hidden md:table-cell' : ''} ${className}`}
        {...props}
    >
        {children}
    </td>
);

interface MoneyCellProps extends Omit<TdProps, 'children' | 'align'> {
    value: number;
    /** Resalta el importe como total */
    emphasis?: boolean;
    /** Formateador; por defecto agrupa con comas y sin decimales */
    format?: (n: number) => string;
}

/** Celda de dinero — mono tabular, alineada a la derecha. */
export const MoneyCell: React.FC<MoneyCellProps> = ({
    value, emphasis = false, format, className = '', ...props
}) => (
    <Td
        align="right"
        className={`font-data tabular-nums whitespace-nowrap ${emphasis ? 'font-semibold text-navy-950' : 'text-navy-950'} ${className}`}
        {...props}
    >
        {format ? format(value) : money(value)}
    </Td>
);

/**
 * Contenedor de acciones por fila: invisible en reposo, aparece en hover o
 * cuando algo dentro recibe foco de teclado (accesible, no solo hover).
 * IMPORTANTE: su columna debe tener ancho fijo reservado (ver <ThActions>),
 * para que la fila no cambie de ancho al aparecer las acciones.
 */
export const RowActions: React.FC<React.HTMLAttributes<HTMLDivElement>> = ({ className = '', children, ...props }) => (
    <div
        className={`rp-accion-fila flex items-center justify-end gap-0.5 ${className}`}
        {...props}
    >
        {children}
    </div>
);

/** Encabezado de la columna de acciones — ancho reservado, sin etiqueta visible. */
export const ThActions: React.FC<{ label?: string }> = ({ label = 'Actions' }) => (
    <th className="w-11 py-0 align-middle" scope="col">
        <span className="sr-only">{label}</span>
    </th>
);

/** Barra de progreso compacta para tablas (navy + porcentaje en mono). */
export const ProgressCell: React.FC<{ percent: number; className?: string }> = ({ percent, className = '' }) => (
    <div className={`flex items-center gap-2 justify-end ${className}`}>
        <div className="w-16 h-1 bg-acero-100 rounded-full overflow-hidden shrink-0">
            <div className="h-full bg-navy-800 rounded-full" style={{ width: `${Math.min(100, Math.max(0, percent))}%` }} />
        </div>
        <span className="font-data text-[11px] text-acero-500 tabular-nums w-8 text-right shrink-0">{percent}%</span>
    </div>
);
